import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faFileLines,
  faInbox,
  faFileContract,
  faGavel,
  faBuilding,
  faBoxArchive,
  faEnvelope,
  faBuildingColumns,
} from '@fortawesome/pro-regular-svg-icons';

export interface DetailField {
  /** Label shown in the detail card row */
  label: string;
  /** Dot-path to the value in the API response */
  path: string;
  /** Optional formatter */
  format?: 'date' | 'currency';
}

export interface EntityConfig {
  /** Display label shown in chip (e.g. "Quote", "Policy") */
  label: string;
  /** FontAwesome icon rendered in chip */
  icon: IconDefinition;
  /** Mantine color name for chip background */
  color: string;
  /** API response field name for entity number (shown in chip), null if none */
  numberField: string | null;
  /** API response field paths to try for entity name, in priority order */
  nameFields: string[];
  /** Regex to parse this entity from a context string like "Quote QCB-123 - Acme" */
  pattern: RegExp;
  /** Fields to display in the chip detail hover card */
  detailFields: DetailField[];
}

export const ENTITY_CONFIG: Record<string, EntityConfig> = {
  quote: {
    label: 'Quote',
    icon: faFileLines,
    color: 'blue',
    numberField: 'quoteNumber',
    nameFields: ['companyName', 'firmographicData.name'],
    pattern: /^Quote\s+(.+)$/i,
    detailFields: [
      { label: 'Account', path: 'companyName' },
      { label: 'Status', path: 'status' },
      { label: 'Premium', path: 'premium', format: 'currency' },
      { label: 'Effective', path: 'effectiveDate', format: 'date' },
      { label: 'Product', path: 'productType' },
    ],
  },
  submission: {
    label: 'Submission',
    icon: faInbox,
    color: 'violet',
    numberField: 'submissionNumber',
    nameFields: ['firmographics.name'],
    pattern: /^Submission\s+(.+)$/i,
    detailFields: [
      { label: 'Account', path: 'firmographics.name' },
      { label: 'Status', path: 'status' },
      { label: 'Product', path: 'product' },
      { label: 'Submitted', path: 'created', format: 'date' },
    ],
  },
  policy: {
    label: 'Policy',
    icon: faFileContract,
    color: 'green',
    numberField: 'policyNumber',
    nameFields: ['companyName'],
    pattern: /^Policy\s+(.+)$/i,
    detailFields: [
      { label: 'Account', path: 'companyName' },
      { label: 'Effective', path: 'effectiveDate', format: 'date' },
      { label: 'Expiration', path: 'endDate', format: 'date' },
      { label: 'Premium', path: 'premium', format: 'currency' },
      { label: 'Product', path: 'productType' },
    ],
  },
  claim: {
    label: 'Claim',
    icon: faGavel,
    color: 'orange',
    numberField: 'claimNumber',
    nameFields: ['companyName'],
    pattern: /^Claim\s+(.+)$/i,
    detailFields: [
      { label: 'Account', path: 'companyName' },
      { label: 'Status', path: 'status' },
      { label: 'Loss Date', path: 'lossDate', format: 'date' },
      { label: 'Incident', path: 'lossType' },
    ],
  },
  account: {
    label: 'Account',
    icon: faBuilding,
    color: 'indigo',
    numberField: null,
    nameFields: ['name'],
    pattern: /^Account\s+(.+)$/i,
    detailFields: [
      { label: 'Industry', path: 'industry' },
      { label: 'State', path: 'state' },
    ],
  },
  binder: {
    label: 'Binder',
    icon: faBoxArchive,
    color: 'teal',
    numberField: 'policyNumber',
    nameFields: ['companyName'],
    pattern: /^Binder\s+(.+)$/i,
    detailFields: [
      { label: 'Account', path: 'companyName' },
      { label: 'Effective', path: 'effectiveDate', format: 'date' },
      { label: 'Product', path: 'productType' },
    ],
  },
  cmail: {
    label: 'Email',
    icon: faEnvelope,
    color: 'cyan',
    numberField: null,
    nameFields: ['subject', 'senderName'],
    pattern: /^Email\s+(.+)$/i,
    detailFields: [
      { label: 'From', path: 'senderName' },
      { label: 'Subject', path: 'subject' },
      { label: 'Date', path: 'date', format: 'date' },
    ],
  },
  agency: {
    label: 'Agency',
    icon: faBuildingColumns,
    color: 'grape',
    numberField: null,
    nameFields: ['name', 'agencyName'],
    pattern: /^Agency\s+(.+)$/i,
    detailFields: [
      { label: 'Name', path: 'name' },
      { label: 'State', path: 'state' },
    ],
  },
};

/** Maps alias entity types to their canonical config key */
const ENTITY_ALIASES: Record<string, string> = {
  'uw-review': 'quote',
};

/** Resolve an entity type (including aliases) to its config */
export function getEntityConfig(type: string): EntityConfig | undefined {
  const resolved = ENTITY_ALIASES[type] || type;
  return ENTITY_CONFIG[resolved];
}

/** Resolve an alias to its canonical entity type key */
export function resolveEntityType(type: string): string {
  return ENTITY_ALIASES[type] || type;
}

/**
 * Read a dot-path value from an object (e.g. "firmographicData.name").
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && !Array.isArray(acc)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/**
 * Extract number and name from an entity data object using the config's field mappings.
 * Works with both flat and nested API response objects.
 */
export function extractFields(
  data: Record<string, unknown> | undefined,
  config: EntityConfig
): { number: string; name: string } {
  if (!data) return { number: '', name: '' };

  const number = config.numberField
    ? (getNestedValue(data, config.numberField) as string) || ''
    : '';

  let name = '';
  for (const field of config.nameFields) {
    const val = getNestedValue(data, field);
    if (typeof val === 'string' && val) {
      name = val;
      break;
    }
  }

  return { number, name };
}

/**
 * Build the display string for a chip: "Label Number - Name"
 */
export function buildChipLabel(
  config: EntityConfig,
  number: string,
  name: string
): string {
  if (number && name) return `${config.label} ${number} - ${name}`;
  if (name) return `${config.label} ${name}`;
  if (number) return `${config.label} ${number}`;
  return config.label;
}

/**
 * Parse a context string (e.g. "Quote QCB-123 - Acme") to find its entity type.
 */
export function parseContextType(
  contextString: string
): { type: string; identifier: string } | null {
  for (const [type, config] of Object.entries(ENTITY_CONFIG)) {
    const match = contextString.match(config.pattern);
    if (match) {
      return { type, identifier: match[1] };
    }
  }
  return null;
}
